import styled from '@emotion/styled'
import type { FC } from 'react'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CardBase, * as Card from '@segment/matcha/recipes/Card'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import type { HelpCardRecord } from '../../data'

interface HelpGridProps {
  data: HelpCardRecord[]
}

const copy = {
  noDataMessage: (
    <>
      Sorry, we couldn't find a match for that. Try adjusting the filters above to expand the results.
      <br /> Can't find what you're looking for?
    </>
  ),
}

const noDataLink = {
  text: `Schedule a call with a Segment team member here.`,
  url: `/demo/`,
}

const HelpGrid: FC<HelpGridProps> = ({ data }) => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <SectionBase variant="noCopy">
        <Section.Contents>
          {data.length === 0 ? (
            <CopyTextBase centered={true}>
              <CopyText.Description variant="pLarge">{copy.noDataMessage}</CopyText.Description>
              <CopyText.Link href={getAbsoluteUrl(noDataLink.url, pageConfig)}>{noDataLink.text}</CopyText.Link>
            </CopyTextBase>
          ) : (
            <ColumnTrack columns={1} overrides={{ md: 2, xl: 3 }}>
              {data.map(({ title, description, href, icon: Icon }) => (
                <CardBase key={title} href={getAbsoluteUrl(href, pageConfig)}>
                  <Card.Body>
                    <Card.Accessories>
                      <Card.AccessoriesRow>
                        <IconWrapper variant="transparent">
                          <Icon size="large" />
                        </IconWrapper>
                      </Card.AccessoriesRow>
                    </Card.Accessories>
                    <Card.Copy>
                      <Card.Headline variant="h4" tag="h2">
                        {title}
                      </Card.Headline>
                      <Card.Description variant="pLarge">{description}</Card.Description>
                    </Card.Copy>
                    <Card.Action alignment="right">
                      <ChevronRightIcon />
                    </Card.Action>
                  </Card.Body>
                </CardBase>
              ))}
            </ColumnTrack>
          )}
        </Section.Contents>
      </SectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
`

export default HelpGrid
